import React, { useState, useEffect, useContext } from "react";
import { Menu, Divider } from "@mantine/core";
import { Bell } from "lucide-react";
import "./notifications.css";
import { GlobalContext } from "../../GlobalContext";
import { useNavigate } from "react-router-dom";

const Notifications = () => {
  const navigate = useNavigate();
  const { fetchNotificationsCount, notify } = useContext(GlobalContext);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/notifications/ui";
  const MARK_ALL_READ_ENDPOINT = "/wp-json/dbsb/v1/notifications/mark-all-read";
  const MARK_READ_ENDPOINT = "/wp-json/dbsb/v1/notifications";

  useEffect(() => {
    // Initial fetch
    const fetchNotifications = async () => {
      setIsLoading(true);
      setApiError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error("Authentication token not found.");
        }

        const response = await fetch(API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: { "X-WP-Nonce": window.wpApiSettings.nonce },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch notifications");
        }

        const data = await response.json();
        setNotifications(
          data.map((notif) => ({
            id: notif.id,
            text: notif.text,
            time: formatTimeAgo(notif.created_at),
            read: notif.read,
            link: notif.link,
            created_at: notif.created_at, // Store raw timestamp
          }))
        );
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setApiError(error.message);
        setNotifications([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();

    // Refresh timestamps every 60 seconds
    const intervalId = setInterval(() => {
      setNotifications((prev) =>
        prev.map((notif) => ({
          ...notif,
          time: formatTimeAgo(notif.created_at),
        }))
      );
    }, 60000);

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  const markAllAsRead = async () => {
    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(MARK_ALL_READ_ENDPOINT, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to mark all notifications as read"
        );
      }

      setNotifications(
        notifications.map((notif) => ({ ...notif, read: true }))
      );
      await fetchNotificationsCount();
      notify("NOTIFICATIONS_UPDATED");
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      setApiError(error.message);
    }
  };

  const handleNotificationClick = async (id, link) => {
    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(`${MARK_READ_ENDPOINT}/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to mark notification as read"
        );
      }

      setNotifications(
        notifications.map((notif) =>
          notif.id === id ? { ...notif, read: true } : notif
        )
      );
      await fetchNotificationsCount();
      notify("NOTIFICATIONS_UPDATED");

      if (link) {
        window.location.href = link;
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      setApiError(error.message);
    }
  };

  const formatTimeAgo = (utcTime) => {
    const now = new Date();
    const created = new Date(utcTime);
    const diffMs = now - created;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return `${diffSeconds} second${diffSeconds === 1 ? '' : 's'} ago`;
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  };

  const hasNotifications = notifications.length > 0;
  const hasUnread = notifications.some((notif) => !notif.read);

  return (
    <Menu shadow="md" width={350} position="bottom-end">
      <Menu.Target>
        <button className="header-icon-button">
          <Bell size={20} className="header-icon" />
        </button>
      </Menu.Target>
      <Menu.Dropdown className="notifications-dropdown">
        <div className="notifications-header">
          <span className="notifications-title">Notifications</span>
          <div className="notifications-actions">
            <button
              className="notifications-action"
              onClick={markAllAsRead}
              disabled={!hasNotifications || !hasUnread}
              style={{ opacity: !hasNotifications || !hasUnread ? 0.7 : 1 }}
            >
              Mark all as read
            </button>
          </div>
        </div>
        <Divider />
        <div className="notifications-body">
          {isLoading ? (
            <div className="notifications-loading">
              <p>Loading notifications...</p>
            </div>
          ) : apiError ? (
            <div className="notifications-error">
              <p>Failed to load notifications: {apiError}</p>
            </div>
          ) : hasNotifications ? (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className="notification-item"
                onClick={() => handleNotificationClick(notif.id, notif.link)}
                style={{ cursor: notif.link ? "pointer" : "default" }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/assets/notification-arrow.svg`}
                  alt=""
                  className="notification-icon"
                />
                <div
                  className="notification-content"
                  style={{ opacity: notif.read ? 0.6 : 1 }}
                >
                  <p className="notification-text">{notif.text}</p>
                  <p className="notification-time">{notif.time}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="notifications-empty">
              <div className="notifications-empty-icon">
                <Bell size={28} color="#FFFFFF" />
              </div>
              <p className="notifications-empty-text">
                There are no new notifications
              </p>
            </div>
          )}
        </div>
      </Menu.Dropdown>
    </Menu>
  );
};

export default Notifications;
